import { useTranslation } from '../../hooks/useTranslation'
import Money from '../../design/Money'
import type { Cart } from '../../types/display'

export default function CartFooter({ cart, currency }: { cart: Cart; currency: string }) {
  const { t } = useTranslation()
  const money = (amount: number | undefined) => <Money amount={amount ?? 0} currency={currency} />
  const rowClass = 'flex items-baseline justify-between space-x-4 text-cfd-detail text-text-mid'

  return (
    <div className="border-t border-divider bg-white px-cfd-gutter pb-cfd-4 pt-cfd-3">
      <div className="flex flex-col space-y-cfd-1">
        <div className={rowClass}>
          <span>{t('total')}</span>
          <span className="tabular-nums">{money(cart.itemsAmount)}</span>
        </div>
        {/* A discount the customer cannot see is a discount they cannot check.
            The row appears as soon as the cashier names one — a discount code
            priced by the backend arrives labelled before it arrives valued, and
            a blank amount for that moment beats no row at all (#1). */}
        {(!!cart.discountAmount || !!cart.discountLabel) && (
          <div className={`${rowClass} motion-safe:animate-itemIn`}>
            <span className="flex min-w-0 items-baseline space-x-2">
              <span>{t('discount')}</span>
              {!!cart.discountLabel && (
                <span className="truncate rounded-full bg-success/10 px-[0.6em] py-[0.05em] uppercase tracking-[0.08em] text-success">
                  {cart.discountLabel}
                </span>
              )}
            </span>
            {!!cart.discountAmount && (
              <span className="shrink-0 font-volte-semibold tabular-nums text-success">− {money(cart.discountAmount)}</span>
            )}
          </div>
        )}
      </div>

      <div className="mt-cfd-2 flex flex-wrap items-end justify-between">
        <div className="mr-6 pb-[0.6em] text-cfd-label font-medium uppercase text-text-mid">{t('amount-for-payment')}</div>
        <div className="font-volte-semibold text-cfd-total text-primary">
          {/* Set, not rolled: a figure counting up reads as money climbing. */}
          <Money amount={cart.cashAmount ?? 0} currency={currency} lead />
        </div>
      </div>
      {/* VAT is for the receipt, not the decision: a footnote under the total. */}
      <div className="mt-[0.4em] text-right text-cfd-label normal-case tracking-normal text-text-mid">
        {t('of-which-vat')} {money(cart.vatAmount)}
      </div>
    </div>
  )
}
